import { FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import type { TransactionParameters } from '@parkchirp/reporting';
import type { Dayjs } from 'dayjs';
import type { ReactNode } from 'react';

export type TransactionTimeRangeValue = {
	startTime: Dayjs | null;
	endTime: Dayjs | null;
	timezone: string;
};

export const TRANSACTION_REPORT_FACILITY_TIMEZONE = 'facility';

export const TRANSACTION_REPORT_TIMEZONES = [
	{ label: 'Facility Timezone', value: TRANSACTION_REPORT_FACILITY_TIMEZONE },
	{ label: 'Eastern Time', value: 'America/New_York' },
	{ label: 'Central Time', value: 'America/Chicago' },
	{ label: 'Mountain Time', value: 'America/Denver' },
	{ label: 'Pacific Time', value: 'America/Los_Angeles' },
	{ label: 'Alaska Time', value: 'America/Anchorage' },
	{ label: 'Hawaii-Aleutian Time', value: 'Pacific/Honolulu' },
] as const;

type Props = {
	value: TransactionTimeRangeValue;
	onChange: (value: TransactionTimeRangeValue) => void;
	startDateField: ReactNode;
	endDateField: ReactNode;
};

export const initialTransactionTimeRange = (): TransactionTimeRangeValue => ({
	startTime: null,
	endTime: null,
	timezone: TRANSACTION_REPORT_FACILITY_TIMEZONE,
});

export const buildTransactionDisplayTimezone = (
	value: TransactionTimeRangeValue,
): TransactionParameters['displayTimezone'] | undefined => {
	if (value.timezone === TRANSACTION_REPORT_FACILITY_TIMEZONE) {
		return undefined;
	}

	return value.timezone;
};

export const buildTransactionTimeRange = (
	value: TransactionTimeRangeValue,
): TransactionParameters['timeRange'] | undefined => {
	if (!value.startTime && !value.endTime) {
		return undefined;
	}

	return {
		timezone: value.timezone,
		...(value.startTime && { startTime: value.startTime.format('HH:mm') }),
		...(value.endTime && { endTime: value.endTime.format('HH:mm') }),
	};
};

export const TransactionTimeRangeFilter = ({ value, onChange, startDateField, endDateField }: Props) => {
	return (
		<Stack spacing={2}>
			<Typography variant='subtitle1' fontWeight={600}>
				Date/time range
			</Typography>

			<Typography variant='body2' color='text.secondary'>
				Times are optional. Facility Timezone displays each transaction in its facility's local timezone.
			</Typography>

			<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
				{startDateField}

				<TimePicker
					label='Start time'
					value={value.startTime}
					onChange={(startTime) => {
						onChange({
							...value,
							startTime,
						});
					}}
					slotProps={{ textField: { fullWidth: true } }}
				/>

				{endDateField}

				<TimePicker
					label='End time'
					value={value.endTime}
					onChange={(endTime) => {
						onChange({
							...value,
							endTime,
						});
					}}
					slotProps={{ textField: { fullWidth: true } }}
				/>

				<FormControl fullWidth>
					<InputLabel id='transaction-report-timezone-label'>Timezone</InputLabel>
					<Select
						labelId='transaction-report-timezone-label'
						label='Timezone'
						value={value.timezone}
						onChange={(event) => {
							onChange({
								...value,
								timezone: event.target.value,
							});
						}}
					>
						{TRANSACTION_REPORT_TIMEZONES.map((timezone) => (
							<MenuItem key={timezone.value} value={timezone.value}>
								{timezone.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Stack>
		</Stack>
	);
};
